<template>
  <AuthLayout title="Two Step Verification" description="We sent a 6 digits security code to your email address.">
    <div>
      <form @submit.prevent="handleSubmit">
        <div class="space-y-5">
          <!-- OTP Inputs -->
          <div>
            <label aria-labelledby="otp-container" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
              Type your 6 digits security code
            </label>
            <div class="flex gap-2 sm:gap-4" id="otp-container">
              <TextInputDefault
                v-for="index in 6"
                :key="index"
                :id="`otp-${index}`"
                :ref="(el) => inputRefs[index - 1] = getInputElement(el)"
                type="text"
                maxlength="1"
                inputClass="text-center"
                :modelValue="otp[index - 1]"
                @keydown="(e: KeyboardEvent) => handleKeyDown(e, index - 1)"
                @paste="handlePaste"
              />
            </div>
          </div>

          <BaseButton
            type="submit"
            text="Verify My Account"
            variant="primary"
            size="md"
            class="w-full"
            :disabled="!isOtpComplete"
          />

        </div>
      </form>
      <div class="mt-5">
        <p class="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
          Didn't get the code?
          <a href="#" class="text-brand-500 hover:text-brand-600 dark:text-brand-400">Resend</a>
        </p>
      </div>
    </div>
  </AuthLayout>
</template>

<script setup lang="ts">
import { ref, nextTick, computed, onMounted } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import BaseButton from '@/components/Buttons/BaseButton.vue';
import TextInputDefault from '@/components/forms/TextInputDefault.vue';
import AuthLayout from '@/components/layout/AuthLayout.vue';

const otp = ref<string[]>(Array(6).fill(''));
const inputRefs = ref<(HTMLInputElement | null)[]>([]);

// Check if all OTP fields are filled
const isOtpComplete = computed(() => {
  return otp.value.every(digit => digit !== '');
});

// Helper function to extract input element from component ref
const getInputElement = (el: unknown): HTMLInputElement | null => {
  if (!el) return null;

  if (typeof el === 'object' && '$el' in (el as ComponentPublicInstance)) {
    // Vue component instance
    const component = el as ComponentPublicInstance;
    return component.$el?.querySelector('input') || null;
  }

  if (el instanceof HTMLInputElement) {
    // Direct input element
    return el;
  }

  return null;
};

// Handle input changes

// Handle keyboard events
const handleKeyDown = (event: KeyboardEvent, index: number) => {
  // Handle backspace
  if (event.key === 'Backspace') {
    if (otp.value[index] === '') {
      // If current field is empty, focus previous field and clear it
      if (index > 0) {
        otp.value[index - 1] = '';
        nextTick(() => {
          focusInput(index - 1);
        });
      }
    } else {
      // If current field has value, just clear it
      otp.value[index] = '';
    }
    event.preventDefault();
  }

  // Handle arrow keys for navigation
  else if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault();
    focusInput(index - 1);
  }
  else if (event.key === 'ArrowRight' && index < 5) {
    event.preventDefault();
    focusInput(index + 1);
  }

  // Handle tab key - navigate through inputs
  else if (event.key === 'Tab') {
    if (!event.shiftKey && index < 5) {
      event.preventDefault();
      focusInput(index + 1);
    } else if (event.shiftKey && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    }
  }
};

// Handle paste event for bulk OTP entry
const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedData = event.clipboardData?.getData('text') || '';
  const digits = pastedData.replace(/[^0-9]/g, '').slice(0, 6);

  if (digits.length === 6) {
    // Fill all fields with pasted digits
    digits.split('').forEach((digit, index) => {
      otp.value[index] = digit;
    });

    // Focus the last input
    nextTick(() => {
      focusInput(5);
    });
  }
};

// Focus specific input
const focusInput = (index: number) => {
  const input = inputRefs.value[index];
  if (input) {
    input.focus();
    input.select();
  }
};

// Handle form submission
const handleSubmit = () => {
  if (isOtpComplete.value) {
    const otpCode = otp.value.join('');
    console.log('OTP Submitted:', otpCode);
    // API call would go here
  }
};

// Auto-focus first input when component mounts
onMounted(() => {
  nextTick(() => {
    focusInput(0);
  });
});
</script>
